<template>
  <LocationValueList
    :icon="config.icon"
    :rows="rows"
    :title="config.label"
    :total="totalRow" />
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import type { DisplayConfig, LocationValueRow, MetricComparisonProps, MetricValue } from '@/models/metricDefinitions.ts';
import { formatSubValue, formatValue, formatValueWithTarget } from '@/utils/formatters';
import LocationValueList from './LocationValueList.vue';

const props = defineProps<MetricComparisonProps>();

const comparisonConfig = computed(() => ({
  ...props.config,
  icon: undefined
}));

const getDisplayValue = (value: number | Date | null, target?: number | '?', config: DisplayConfig = comparisonConfig.value) => {
  if (target !== undefined) {
    return formatValueWithTarget(value, target, config);
  }
  return formatValue(value, config);
};

// A configured second metric takes precedence over the value of the metric itself in its second unit.
const getSubText = (value: MetricValue, subValue?: MetricValue): string | undefined => {
  if (!props.subConfig || !subValue) {
    return value.subAmount === undefined ? undefined : formatSubValue(value.subAmount, props.config);
  }

  return `${props.subConfig.label}: ${getDisplayValue(subValue.amount, subValue.target, props.subConfig)}`;
};

const rows = computed((): LocationValueRow[] =>
  [...props.locations]
    .sort((a, b) => a.name.localeCompare(b.name, 'de'))
    .map((location) => ({
      name: location.name,
      value: getDisplayValue(location.value.amount, location.value.target),
      subText: getSubText(location.value, location.subValue),
      isLoading: location.value.isLoading
    }))
);

const totalRow = computed((): LocationValueRow | undefined => {
  if (!props.total) {
    return undefined;
  }

  return {
    name: props.totalLabel ?? 'Gesamt',
    value: getDisplayValue(props.total.amount, props.total.target),
    subText: getSubText(props.total, props.subTotal),
    isLoading: props.total.isLoading
  };
});
</script>
